import { getAllThreats, bulkUpdateThreats, getAllIpEnrichments, deleteThreats, getAllEmailLogs } from '../services/storage.js';
import { whoisLookup } from '../services/whois.js';
import { updateGeoFromWhois, updateGeoFromShodan } from '../services/geoip.js';
import { checkShodan } from '../services/threat-intel.js';
import { html, SEVERITY_SORT_ASC, concurrencyPool, truncate } from '../services/util.js';
import { ConfirmDialog } from './confirm-dialog.js';
import './toast-container.js';

const PAGE_SIZES = [50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

const template = document.createElement('template');
template.innerHTML = `
  <div class="section-header">
    <h1>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:8px">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      Threat Analysis
    </h1>
    <div class="section-actions">
      <input type="text" id="threat-search" placeholder="Search IP, user, or type..." style="width: 220px;">
      <button class="btn btn-sm btn-secondary" id="threats-refresh">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
        Refresh
      </button>
    </div>
  </div>

  <div class="threat-filters">
    <div class="filter-group">
      <select id="filter-severity">
        <option value="">All Severities</option>
        <option value="critical">Critical</option>
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>
    </div>
    <div class="filter-group">
      <select id="filter-status">
        <option value="">All Statuses</option>
        <option value="investigating">Investigating</option>
        <option value="active">Active</option>
        <option value="resolved">Resolved</option>
      </select>
    </div>
    <div class="filter-group">
      <select id="filter-type">
        <option value="">All Types</option>
        <option value="impossible_travel">Impossible Travel</option>
        <option value="known_bad_ip">Known Bad IP</option>
        <option value="failure_spike">Failure Spike</option>
        <option value="brute_force">Brute Force</option>
        <option value="new_country">New Country</option>
        <option value="anomalous_time">Anomalous Time</option>
        <option value="legacy_auth">Legacy Auth</option>
      </select>
    </div>
  </div>

  <div id="mass-action-bar" class="mass-action-bar" style="display:none">
    <span class="mass-count"><span id="selected-count">0</span> selected</span>
    <span style="color: var(--text-muted); font-size: 0.8rem;">Set status:</span>
    <button class="btn btn-sm btn-secondary" data-mass-status="investigating">Investigating</button>
    <button class="btn btn-sm btn-secondary" data-mass-status="active">Active</button>
    <button class="btn btn-sm btn-secondary" data-mass-status="resolved">Resolved</button>
    <span style="color: var(--text-muted); font-size: 0.8rem;">Actions:</span>
    <button class="btn btn-sm btn-secondary" id="dismiss-selected" title="Dismiss selected threats">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      Dismiss
    </button>
    <button class="btn btn-sm btn-secondary" id="delete-selected-threats" title="Delete selected threats">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
      Delete
    </button>
    <span style="color: var(--text-muted); font-size: 0.8rem;">Geo:</span>
    <button class="btn btn-sm btn-secondary" id="enrich-geo-whois">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
      WHOIS
    </button>
    <button class="btn btn-sm btn-secondary" id="enrich-geo-shodan">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M21 12a9 9 0 01-9 9"/><path d="M3 12a9 9 0 019-9"/><circle cx="12" cy="12" r="3"/></svg>
      Shodan
    </button>
    <button class="btn btn-sm btn-secondary" id="clear-selection">Clear</button>
  </div>

  <div class="pagination-bar">
    <span id="threat-count"></span>
    <span style="flex:1"></span>
    <span style="display:flex;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-muted)">
      Rows:
      <select id="threat-page-size" style="font-size:0.75rem;padding:2px 6px;border-radius:4px;border:1px solid var(--border-color);background:var(--bg-input);color:var(--text-primary)">
        ${PAGE_SIZES.map(s => `<option value="${s}" ${s === DEFAULT_PAGE_SIZE ? 'selected' : ''}>${s}</option>`).join('')}
      </select>
    </span>
    <button class="btn btn-sm btn-secondary" id="page-prev" disabled>Prev</button>
    <span id="page-info"></span>
    <button class="btn btn-sm btn-secondary" id="page-next">Next</button>
  </div>

  <div class="table-wrapper">
    <table class="data-table" id="threat-table">
      <thead>
        <tr>
          <th style="width:36px"><input type="checkbox" id="select-all-threats"></th>
          <th data-sort="severity" class="sortable">Severity <span class="sort-arrow"></span></th>
          <th data-sort="type" class="sortable">Type <span class="sort-arrow"></span></th>
          <th data-sort="ip" class="sortable">IP <span class="sort-arrow"></span></th>
          <th data-sort="user" class="sortable">User <span class="sort-arrow"></span></th>
          <th>Device</th>
          <th data-sort="country" class="sortable">Country <span class="sort-arrow"></span></th>
          <th data-sort="status" class="sortable">Status <span class="sort-arrow"></span></th>
          <th>Details</th>
          <th>Email</th>
          <th data-sort="detectedAt" class="sortable">Detected <span class="sort-arrow"></span></th>
        </tr>
      </thead>
      <tbody id="threat-tbody"></tbody>
    </table>
  </div>

  <div id="threat-empty" class="empty-state">
    <div class="empty-icon">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.4">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    </div>
    <div class="empty-text">No threats detected</div>
    <div class="empty-hint">Threats will appear here after importing and analyzing logs</div>
  </div>
`;

export class ThreatPane extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = template.innerHTML;
    this._threats = [];
    this._enrichments = [];
    this._enrichByIP = {};
    this._page = 0;
    this._pageSize = DEFAULT_PAGE_SIZE;
    this._sortField = 'severity';
    this._sortDir = 1;
    this._debounceTimer = null;
    this._refreshGen = 0;
    this._emailIPs = new Set();
  }

  disconnectedCallback() {
    clearTimeout(this._debounceTimer);
  }

  connectedCallback() {
    this.querySelector('#filter-severity').addEventListener('change', () => this.refresh());
    this.querySelector('#filter-status').addEventListener('change', () => this.refresh());
    this.querySelector('#filter-type').addEventListener('change', () => this.refresh());
    this.querySelector('#threat-search').addEventListener('input', () => this.debounceRefresh());
    this.querySelector('#threats-refresh').addEventListener('click', () => this.refresh());

    this.querySelector('#threat-page-size').addEventListener('change', (e) => {
      this._pageSize = Number(e.target.value);
      this._page = 0;
      this.refresh();
    });

    this.querySelector('#select-all-threats').addEventListener('change', (e) => {
      this.querySelectorAll('.threat-checkbox').forEach(cb => cb.checked = e.target.checked);
      this.updateMassActionBar();
    });

    this.querySelector('#clear-selection').addEventListener('click', () => this.clearSelection());

    this.querySelector('#dismiss-selected').addEventListener('click', () => this.dismissSelected());
    this.querySelector('#delete-selected-threats').addEventListener('click', () => this.deleteSelected());

    this.querySelector('#enrich-geo-whois').addEventListener('click', () => this.enrichSelectedGeo('whois'));
    this.querySelector('#enrich-geo-shodan').addEventListener('click', () => this.enrichSelectedGeo('shodan'));

    this.querySelectorAll('[data-mass-status]').forEach(btn => {
      btn.addEventListener('click', async () => {
        await this.applyMassStatus(btn.dataset.massStatus);
      });
    });

    this.querySelector('#page-prev').addEventListener('click', () => {
      if (this._page > 0) { this._page--; this.render(); }
    });
    this.querySelector('#page-next').addEventListener('click', () => {
      const totalPages = Math.ceil(this._threats.length / this._pageSize);
      if (this._page < totalPages - 1) { this._page++; this.render(); }
    });

    this.querySelectorAll('.sortable').forEach(th => {
      th.addEventListener('click', () => this.sortBy(th.dataset.sort));
    });

    document.addEventListener('threatscope:stats-update', () => this.refresh());
    document.addEventListener('threatscope:data-cleared', () => { this._threats = []; this._page = 0; this.render(); });

    this.refresh();
  }

  debounceRefresh() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.refresh(), 250);
  }

  sortBy(field) {
    if (this._sortField === field) {
      this._sortDir *= -1;
    } else {
      this._sortField = field;
      this._sortDir = 1;
    }
    this.render();
  }

  async refresh() {
    const gen = ++this._refreshGen;
    const severity = this.querySelector('#filter-severity').value;
    const status = this.querySelector('#filter-status').value;
    const type = this.querySelector('#filter-type').value;
    const search = (this.querySelector('#threat-search').value || '').toLowerCase();

    const filters = {};
    if (severity) filters.severity = severity;
    if (status) filters.status = status;
    if (type) filters.type = type;

    const threats = await getAllThreats(filters);
    if (gen !== this._refreshGen) return;

    const enrichments = await getAllIpEnrichments();
    if (gen !== this._refreshGen) return;

    const enrichByIP = {};
    enrichments.forEach(e => { enrichByIP[e.ip] = e; });

    let filtered = threats;

    if (search) {
      filtered = filtered.filter(t => {
        const enrich = enrichByIP[t.ip];
        const country = enrich ? enrich.country : '';
        return (t.ip || '').toLowerCase().includes(search) ||
          (t.user || '').toLowerCase().includes(search) ||
          (t.type || '').toLowerCase().includes(search) ||
          (this.formatDetails(t) || '').toLowerCase().includes(search) ||
          (country || '').toLowerCase().includes(search);
      });
    }

    if (gen !== this._refreshGen) return;

    // Load email log IPs for cross-reference
    const emailLogs = await getAllEmailLogs();
    if (gen !== this._refreshGen) return;
    const emailIPs = new Set();
    emailLogs.forEach(el => { if (el.senderIp) emailIPs.add(el.senderIp); });
    this._emailIPs = emailIPs;

    this._threats = filtered;
    this._enrichments = enrichments;
    this._enrichByIP = enrichByIP;
    this._page = 0;
    this.render();
  }

  render() {
    const tbody = this.querySelector('#threat-tbody');
    const empty = this.querySelector('#threat-empty');
    const enrichByIP = this._enrichByIP;

    if (this._threats.length === 0) {
      tbody.innerHTML = '';
      empty.style.display = '';
      this.querySelector('.pagination-bar').style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    this.querySelector('.pagination-bar').style.display = 'flex';

    // Sort
    const sorted = [...this._threats].sort((a, b) => {
      let aVal, bVal;
      if (this._sortField === 'severity') {
        aVal = SEVERITY_SORT_ASC[a.severity] ?? 99;
        bVal = SEVERITY_SORT_ASC[b.severity] ?? 99;
      } else if (this._sortField === 'country') {
        const ea = enrichByIP[a.ip];
        const eb = enrichByIP[b.ip];
        aVal = (ea && ea.country) || '';
        bVal = (eb && eb.country) || '';
      } else if (this._sortField === 'detectedAt') {
        aVal = a.detectedAt || '';
        bVal = b.detectedAt || '';
      } else if (this._sortField === 'type') {
        aVal = this.formatType(a.type || '');
        bVal = this.formatType(b.type || '');
      } else {
        aVal = (a[this._sortField] || '').toLowerCase();
        bVal = (b[this._sortField] || '').toLowerCase();
      }
      if (aVal < bVal) return -1 * this._sortDir;
      if (aVal > bVal) return 1 * this._sortDir;
      return 0;
    });

    const totalPages = Math.ceil(sorted.length / this._pageSize);
    const start = this._page * this._pageSize;
    const pageThreats = sorted.slice(start, start + this._pageSize);

    this.querySelector('#threat-count').textContent = `${sorted.length} threats`;
    this.querySelector('#page-info').textContent = `Page ${this._page + 1} of ${totalPages || 1}`;
    this.querySelector('#page-prev').disabled = this._page === 0;
    this.querySelector('#page-next').disabled = this._page >= totalPages - 1;

    this.querySelectorAll('.sortable').forEach(th => {
      const arrow = th.querySelector('.sort-arrow');
      if (th.dataset.sort === this._sortField) {
        arrow.textContent = this._sortDir === 1 ? ' \u25B2' : ' \u25BC';
        arrow.style.opacity = '1';
      } else {
        arrow.textContent = '';
      }
    });

    tbody.innerHTML = pageThreats.map(t => {
      const enrich = enrichByIP[t.ip];
      const country = enrich ? enrich.country : '';

      return `<tr class="clickable-row" data-id="${t.id}">
        <td class="checkbox-cell"><input type="checkbox" class="threat-checkbox" data-id="${t.id}"></td>
        <td>
          <span class="severity-dot ${t.severity}"></span>
          <span class="badge badge-severity-${t.severity}">${t.severity}</span>
        </td>
        <td><code>${this.formatType(t.type)}</code></td>
        <td><code>${html(t.ip)}</code></td>
        <td title="${html(t.user)}">${html(truncate(t.user, 20))}</td>
        <td>${t.deviceId ? '<span class="badge badge-entra-registered" title="Device is registered with Entra ID">Entra Registered</span>' : '-'}</td>
        <td>${country || '-'}</td>
        <td>
          <select class="status-select" data-id="${t.id}">
            <option value="investigating" ${t.status === 'investigating' ? 'selected' : ''}>Investigating</option>
            <option value="active" ${t.status === 'active' ? 'selected' : ''}>Active</option>
            <option value="resolved" ${t.status === 'resolved' ? 'selected' : ''}>Resolved</option>
          </select>
        </td>
        <td>${this.formatDetails(t)}</td>
        <td>${this._emailIPs.has(t.ip) ? '<span class="badge badge-email-ref" title="This IP also appears in email logs">📧 Email</span>' : '-'}</td>
        <td>${t.detectedAt ? new Date(t.detectedAt).toLocaleString() : '-'}</td>
      </tr>`;
    }).join('');

    tbody.querySelectorAll('.clickable-row').forEach(row => {
      row.addEventListener('click', (e) => {
        if (e.target.tagName === 'SELECT' || e.target.type === 'checkbox') return;
        const id = Number(row.dataset.id);
        const threat = this._threats.find(t => t.id === id);
        if (threat) {
          const enrich = enrichByIP[threat.ip];
          document.dispatchEvent(new CustomEvent('threatscope:show-detail', {
            detail: { type: 'threat', threat, enrichment: enrich },
          }));
        }
      });
    });

    tbody.querySelectorAll('.status-select').forEach(sel => {
      sel.addEventListener('change', async () => {
        const id = Number(sel.dataset.id);
        const newStatus = sel.value;
        await bulkUpdateThreats([id], { status: newStatus, classifiedAt: new Date().toISOString() });
        document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
      });
    });

    tbody.querySelectorAll('.threat-checkbox').forEach(cb => {
      cb.addEventListener('change', () => this.updateMassActionBar());
    });
  }

  updateMassActionBar() {
    const selected = this.querySelectorAll('.threat-checkbox:checked');
    const bar = this.querySelector('#mass-action-bar');
    const count = this.querySelector('#selected-count');

    if (selected.length > 0) {
      bar.style.display = 'flex';
      count.textContent = selected.length;
    } else {
      bar.style.display = 'none';
    }
  }

  async applyMassStatus(status) {
    const checkboxes = this.querySelectorAll('.threat-checkbox:checked');
    const ids = [...checkboxes].map(cb => Number(cb.dataset.id));

    if (ids.length === 0) return;

    await bulkUpdateThreats(ids, {
      status,
      classifiedAt: new Date().toISOString(),
    });

    this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
  }

  async dismissSelected() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) return;

    const confirmed = await ConfirmDialog.show({
      title: 'Dismiss Threats',
      message: `Dismiss ${ids.length} selected threat${ids.length === 1 ? '' : 's'} (set to resolved)?`,
      confirmText: 'Dismiss',
    });
    if (!confirmed) return;

    await bulkUpdateThreats(ids, {
      status: 'resolved',
      classifiedAt: new Date().toISOString(),
    });
    this.clearSelection();
    this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
    window.showToast(`${ids.length} threat${ids.length === 1 ? '' : 's'} dismissed`, 'success');
  }

  async deleteSelected() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) return;

    const confirmed = await ConfirmDialog.show({
      title: 'Delete Threats',
      message: `Permanently delete ${ids.length} selected threat${ids.length === 1 ? '' : 's'}? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    await deleteThreats(ids);
    this.clearSelection();
    this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
    window.showToast(`${ids.length} threat${ids.length === 1 ? '' : 's'} deleted`, 'info');
  }

  async enrichSelectedGeo(source) {
    const checkboxes = this.querySelectorAll('.threat-checkbox:checked');
    const ids = [...checkboxes].map(cb => Number(cb.dataset.id));
    if (ids.length === 0) return;

    const threats = this._threats.filter(t => ids.includes(t.id));
    const enrichByIP = this._enrichByIP;
    let ips = [...new Set(threats.map(t => t.ip).filter(ip => {
      const enrich = enrichByIP[ip];
      return !enrich || !enrich.country;
    }))];

    if (ips.length === 0) {
      window.showToast('All selected IPs already have geo data', 'info');
      this.clearSelection();
      return;
    }

    const label = source === 'whois' ? 'WHOIS' : 'Shodan';
    const confirmed = await ConfirmDialog.show({
      title: `Enrich Geo from ${label}`,
      message: `Look up ${label} records for ${ips.length} IP${ips.length === 1 ? '' : 's'}?`,
      confirmText: 'Enrich',
    });
    if (!confirmed) return;

    window.showToast(`Enriching ${ips.length} IP${ips.length === 1 ? '' : 's'} from ${label}...`, 'info', 0);

    const CONCURRENCY = source === 'whois' ? 5 : 2;
    let enriched = 0;

    const { failures } = await concurrencyPool(ips, async (ip) => {
      if (source === 'whois') {
        const whoisData = await whoisLookup(ip);
        if (whoisData) {
          const result = await updateGeoFromWhois(ip, whoisData);
          if (result) enriched++;
        }
      } else {
        let shodanData = enrichByIP[ip]?.threatIntel?.shodan;
        if (!shodanData?.countryName) {
          shodanData = await checkShodan(ip);
        }
        if (shodanData?.countryName) {
          const result = await updateGeoFromShodan(ip, shodanData);
          if (result) enriched++;
        }
      }
    }, CONCURRENCY);

    const msg = `Geo data enriched for ${enriched} IP${enriched === 1 ? '' : 's'} from ${label}`;
    window.showToast(
      failures > 0 && failures === ips.length ? `${msg} (all failed)` : msg,
      enriched > 0 ? 'success' : 'info'
    );
    this.clearSelection();
    await this.refresh();
    document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
  }

  clearSelection() {
    this.querySelectorAll('.threat-checkbox').forEach(cb => cb.checked = false);
    this.querySelector('#select-all-threats').checked = false;
    this.updateMassActionBar();
  }

  getSelectedIds() {
    return [...this.querySelectorAll('.threat-checkbox:checked')].map(cb => Number(cb.dataset.id));
  }

  formatType(type) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatDetails(t) {
    if (!t.details) return '-';
    const d = t.details;
    const geo = d.ipCountry ? `${d.ipCountry}` : '';
    const geoTag = geo ? ` [${geo}]` : '';

    if (t.type === 'impossible_travel') {
      return `${d.distanceKm}km in ${d.timeDiffMinutes}min${geoTag}`;
    }
    if (t.type === 'known_bad_ip') {
      const label = d.source === 'GreyNoise' ? d.classification : `${d.score || d.pulses || ''}`;
      return `${d.source}: ${label}${geoTag}`;
    }
    if (t.type === 'failure_spike') {
      return `${d.failureCount} failures in ${d.timeWindowMinutes}min${geoTag}`;
    }
    if (t.type === 'brute_force') {
      return `${d.failureCount} failures${geoTag}`;
    }
    if (t.type === 'new_country') {
      return `${d.country || ''}${geoTag}`;
    }
    if (t.type === 'anomalous_time') {
      return `Hour ${d.hour} UTC${geoTag}`;
    }
    if (t.type === 'legacy_auth') {
      return `${d.clientApp || ''}${geoTag}`;
    }
    return '-';
  }

}

customElements.define('threat-pane', ThreatPane);
